import { MAX_PLATE_VALUE_LEN } from './compare';
import { normalizePlate, ocrTokens } from './normalize';
import type { PlateReading, VerdictField } from './types';

/** Shortest model/serial OCR may confirm. Shorter values appear by chance in most plate text. */
export const MIN_OCR_CONFIRM_LEN = 4;
/** Shortest make OCR may confirm ("GM" is a real make; one letter is noise). */
const MIN_MAKE_CONFIRM_LEN = 2;
/** Most adjacent OCR words a make may span ("JOHN DEERE", "DETROIT DIESEL", "MECC ALTE"). */
const MAX_MAKE_WORDS = 4;
/** Most adjacent OCR tokens a model/serial may span (plates print "G 0071 710"). */
const MAX_VALUE_TOKENS = 3;

export type Confirmation = 'barcode' | 'ocr' | null;

export interface Confirmer {
  /** Which independent source confirms this reading's value for `field`: the barcode (serial only, exact), OCR, or none. */
  of(r: PlateReading, field: VerdictField): Confirmation;
}

/** True when `v` equals 1..maxSpan ADJACENT tokens joined: whole tokens only, never part of one. */
function tokensSpell(tokens: readonly string[], v: string, maxSpan: number): boolean {
  for (let i = 0; i < tokens.length; i++) {
    let joined = '';
    for (let j = i; j < Math.min(tokens.length, i + maxSpan); j++) {
      joined += tokens[j];
      if (joined === v) return true;
      if (joined.length >= v.length) break;
    }
  }
  return false;
}

function confirmOnce(r: PlateReading, field: VerdictField, tokens: () => readonly string[]): Confirmation {
  const v = normalizePlate(r.fields?.[field]?.value);
  if (!v || v.length > MAX_PLATE_VALUE_LEN) return null;
  if (field === 'serial' && r.barcodeText && normalizePlate(r.barcodeText) === v) return 'barcode';
  if (field === 'make') return v.length >= MIN_MAKE_CONFIRM_LEN && tokensSpell(tokens(), v, MAX_MAKE_WORDS) ? 'ocr' : null;
  return v.length >= MIN_OCR_CONFIRM_LEN && tokensSpell(tokens(), v, MAX_VALUE_TOKENS) ? 'ocr' : null;
}

/** A confirmer that tokenizes each reading's OCR text, and decides each (reading, field), at most once. Scope it to one verdict. */
export function createConfirmer(): Confirmer {
  const tokenCache = new Map<PlateReading, readonly string[]>();
  const resultCache = new Map<PlateReading, Map<VerdictField, Confirmation>>();
  const tokensOf = (r: PlateReading): readonly string[] => {
    const hit = tokenCache.get(r);
    if (hit) return hit;
    const made = ocrTokens(r.ocrText);
    tokenCache.set(r, made);
    return made;
  };
  return {
    of(r, field) {
      const perReading = resultCache.get(r) ?? new Map<VerdictField, Confirmation>();
      resultCache.set(r, perReading);
      if (perReading.has(field)) return perReading.get(field) ?? null;
      const result = confirmOnce(r, field, () => tokensOf(r));
      perReading.set(field, result);
      return result;
    },
  };
}
